import type { Breadcrumb } from '@bugsnag/js';
import sanitizeUrl, { type SanitizeUrlOptions } from 'utils/sanitize-url';

/*
 * Breadcrumb metadata fields holding a URL: `url` on request breadcrumbs, `to`
 * and `from` on navigation breadcrumbs. Both come from @bugsnag/browser's own
 * network and navigation plugins.
 */
const URL_KEYS = [
	'from',
	'to',
	'url',
];

/*
 * History state objects, which vue-router fills with the full `back`/`current`/
 * `forward` paths, query strings included. They only duplicate `to`/`from`, and
 * redacting them would mean walking arbitrary nested state, so they are dropped.
 */
const DROPPED_KEYS = [
	'prevState',
	'state',
];

/**
 * Strips credentials, query strings and fragments from the URLs BugSnag attaches
 * to breadcrumbs. Creator receives access codes, user tokens and email addresses
 * as query parameters (`settings/url-parameters`), so request and navigation
 * breadcrumbs would otherwise carry them into every error event.
 *
 * @param breadcrumb - The breadcrumb about to be recorded, sanitized in place.
 * @param options - URL sanitization options shared with other telemetry paths.
 */
export default function sanitizeBreadcrumbUrls(
	breadcrumb: Breadcrumb,
	options: SanitizeUrlOptions = {},
): void {
	if (!breadcrumb.metadata) {
		return;
	}

	URL_KEYS.forEach((key) => {
		const value: unknown = breadcrumb.metadata[key];

		if (typeof value === 'string') {
			breadcrumb.metadata[key] = sanitizeUrl(
				value,
				options,
			);
		}
	});

	DROPPED_KEYS.forEach((key) => {
		delete breadcrumb.metadata[key];
	});
}
